import { ChevronRightIcon, PiggyBankIcon, ReceiptTextIcon } from 'lucide-react';
import { useId } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { isSavingsGroup } from '../savings/SavingsPickerSheet';
import { dayLabel } from './dates';
import { amountClass, formatAmount, type PurchaseItem, PurchaseTags } from './PurchaseRow';

/**
 * Everything you can do with one purchase: change category, mark it as
 * savings or a transfer, leave it out of the budget, hide it from lists, or
 * turn it into a recurring bill.
 */
export function PurchaseDetailSheet({
  row,
  open,
  onOpenChange,
  pending,
  onPickCategory,
  onMarkSavings,
  onSetTransfer,
  onSetExcluded,
  onSetHidden,
  onCreateBill,
}: {
  row: PurchaseItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: boolean;
  onPickCategory: () => void;
  /** Opens the savings pot picker */
  onMarkSavings: () => void;
  onSetTransfer: (isTransfer: boolean) => void;
  onSetExcluded: (isExcluded: boolean) => void;
  onSetHidden: (isHidden: boolean) => void;
  onCreateBill: () => void;
}) {
  const transferId = useId();
  const includeId = useId();
  const showId = useId();
  const canCreateBill = row && !row.billName && !row.isTransfer;
  // Only counts as saved while it isn't flagged as a transfer
  const savedIn =
    row?.groupName && isSavingsGroup(row.groupName) && !row.isTransfer ? row.categoryName : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-5 sm:max-w-md">
        {row && (
          <>
            <DialogHeader className="pr-10">
              <DialogTitle className="line-clamp-3 text-[15px] leading-snug font-semibold">
                {row.description}
              </DialogTitle>
              <DialogDescription>
                {dayLabel(row.date)}
                {row.accountName ? ` · ${row.accountName}` : ''}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <p
                className={cn(
                  'text-3xl leading-none font-semibold tracking-tight tabular-nums',
                  amountClass(row),
                )}
              >
                {formatAmount(row.amount)}
              </p>
              <div className="flex flex-wrap gap-1.5">
                <PurchaseTags row={row} />
              </div>
            </div>

            <div className="divide-y divide-border rounded-xl border border-border">
              <button
                type="button"
                onClick={onPickCategory}
                className="flex min-h-12 w-full items-center justify-between gap-3 rounded-t-xl px-3 text-left outline-none hover:bg-muted/60 focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                <span className="text-sm text-muted-foreground">Kategori</span>
                <span className="flex min-w-0 items-center gap-1 text-sm font-medium">
                  <span className={cn('truncate', !row.categoryName && 'text-warning')}>
                    {row.categoryName
                      ? row.groupName
                        ? `${row.groupName} → ${row.categoryName}`
                        : row.categoryName
                      : 'Välj kategori'}
                  </span>
                  <ChevronRightIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                </span>
              </button>

              {/* A row left out of the budget can't count as savings either */}
              {!row.isExcluded && (
                <button
                  type="button"
                  onClick={onMarkSavings}
                  disabled={pending}
                  className="flex min-h-14 w-full items-center justify-between gap-3 px-3 py-2 text-left outline-none hover:bg-muted/60 focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-60"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <PiggyBankIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">
                        {savedIn ? `Sparande · ${savedIn}` : 'Markera som sparande'}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {savedIn ? 'Byt sparpott' : 'Räknas som sparat, inte som köp'}
                      </span>
                    </span>
                  </span>
                  <ChevronRightIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                </button>
              )}

              <div className="flex min-h-14 items-center justify-between gap-3 px-3 py-2">
                <label htmlFor={transferId} className="min-w-0">
                  <span className="block text-sm font-medium">Överföring mellan egna konton</span>
                  <span className="block text-xs text-muted-foreground">Räknas inte som köp</span>
                </label>
                <Switch
                  id={transferId}
                  checked={row.isTransfer}
                  disabled={pending}
                  onCheckedChange={(checked) => onSetTransfer(checked)}
                />
              </div>

              <div className="flex min-h-14 items-center justify-between gap-3 px-3 py-2">
                <label htmlFor={includeId} className="min-w-0">
                  <span className="block text-sm font-medium">Räkna med i budgeten</span>
                  <span className="block text-xs text-muted-foreground">
                    Stäng av för engångsposter som inte ska påverka
                  </span>
                </label>
                <Switch
                  id={includeId}
                  checked={!row.isExcluded}
                  disabled={pending}
                  onCheckedChange={(checked) => onSetExcluded(!checked)}
                />
              </div>

              <div className="flex min-h-14 items-center justify-between gap-3 px-3 py-2">
                <label htmlFor={showId} className="min-w-0">
                  <span className="block text-sm font-medium">Visa i listor</span>
                  <span className="block text-xs text-muted-foreground">
                    Stäng av för att dölja köpet. Det räknas ändå med överallt.
                  </span>
                </label>
                <Switch
                  id={showId}
                  checked={!row.isHidden}
                  disabled={pending}
                  onCheckedChange={(checked) => onSetHidden(!checked)}
                />
              </div>

              {canCreateBill && (
                <button
                  type="button"
                  onClick={onCreateBill}
                  disabled={!row.categoryId}
                  className="flex min-h-12 w-full items-center justify-between gap-3 rounded-b-xl px-3 text-left outline-none hover:bg-muted/60 focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <ReceiptTextIcon
                      aria-hidden
                      className="size-4 shrink-0 text-muted-foreground"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">Skapa räkning</span>
                      <span className="block text-xs text-muted-foreground">
                        {row.categoryId
                          ? 'Följ köpet som en återkommande räkning'
                          : 'Välj en kategori först'}
                      </span>
                    </span>
                  </span>
                  <ChevronRightIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                </button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
